import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { shortGNF } from "@/lib/format";
import { fileUrl } from "@/lib/api";
import { useGuinea, allPrefectures, norm } from "@/lib/guinea";

const DEFAULT_CENTER = [10.4, -11.5];
const DEFAULT_ZOOM = 6;

// Fix default icon issue with webpack
const defaultIcon = new L.DivIcon({
  className: "custom-map-marker",
  html: `<div style="background:hsl(7,56%,50%);color:white;border-radius:50% 50% 50% 0;width:30px;height:30px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;font-weight:bold;box-shadow:0 4px 12px rgba(0,0,0,0.3);border:2px solid white;"><span style="transform:rotate(45deg);font-size:14px;">🏠</span></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

// Cluster listings by city with slight offset for multiple listings in same city
function clusterByCity(listings, CITY_COORDS) {
  const groups = {};
  const withCoords = [];
  listings.forEach((l) => {
    // Use precise coords when available
    if (l.latitude != null && l.longitude != null) {
      withCoords.push({ listing: l, position: [l.latitude, l.longitude] });
      return;
    }
    const key = norm(l.city);
    if (!CITY_COORDS[key]) return;
    if (!groups[key]) groups[key] = [];
    groups[key].push(l);
  });
  const markers = [...withCoords];
  Object.entries(groups).forEach(([city, items]) => {
    const [lat, lng] = CITY_COORDS[city];
    items.forEach((l, idx) => {
      const angle = (idx * 2 * Math.PI) / Math.max(items.length, 1);
      const offset = idx === 0 ? 0 : 0.02;
      markers.push({
        listing: l,
        position: [lat + offset * Math.cos(angle), lng + offset * Math.sin(angle)],
      });
    });
  });
  return markers;
}

function FitBounds({ markers, focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus && markers.length === 0) {
      map.setView(focus, 11);
      return;
    }
    if (markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map((m) => m.position));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
    if (focus && markers.length === 1) map.setView(markers[0].position, 12);
  }, [markers, map, focus]);
  return null;
}

export default function ListingsMap({ listings = [], focus = null }) {
  const geo = useGuinea();
  const coords = useMemo(
    () => Object.fromEntries(allPrefectures(geo).map((p) => [norm(p.name), [p.lat, p.lng]])),
    [geo]
  );
  const markers = useMemo(() => clusterByCity(listings, coords), [listings, coords]);

  return (
    <div className="overflow-hidden rounded-2xl border border-border" data-testid="listings-map">
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: "420px", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {markers.map(({ listing, position }) => (
          <Marker key={listing.listing_id} position={position} icon={defaultIcon}>
            <Popup>
              <div className="w-52">
                {listing.photos?.[0] && (
                  <img
                    src={fileUrl(listing.photos[0])}
                    alt=""
                    className="mb-2 h-24 w-full rounded-lg object-cover"
                  />
                )}
                <div className="font-bold text-primary">{shortGNF(listing.price)}</div>
                <div className="line-clamp-1 text-sm font-semibold">{listing.title}</div>
                <div className="text-xs text-muted-foreground">
                  {listing.quartier}, {listing.city}
                </div>
                <Link
                  to={`/annonce/${listing.listing_id}`}
                  className="mt-2 inline-block text-xs font-bold text-primary underline"
                >
                  Voir l'annonce →
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
        <FitBounds markers={markers} focus={focus} />
      </MapContainer>
    </div>
  );
}
